// Lib
import React from 'react';
import { LogOut, Settings } from 'lucide-react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';

// Actions
import { UserLogout } from '@actions/user';
import { AccountLogout } from '@actions/account';

// UI
import { Avatar, AvatarFallback, AvatarImage } from '@components/ui/avatar';
import { Separator } from '@components/ui/separator';

/* Account anchor (bottom of the sidebar): the "who am I" scope — personal
 * account settings and sign out. Organization switching / org settings live in
 * the top anchor (`OrgMenu`). */

interface AccountProfile {
  name?: string;
  username?: string;
  email?: string;
  avatar?: { url?: string };
}

interface AccountPopoverProps {
  profile?: AccountProfile;
  onClose: () => void;
}

const rowClass =
  'flex w-full items-center gap-2 rounded-lg p-1.5 text-left cursor-pointer hover:bg-marble-bg-50';

const initials = (name?: string): string =>
  (name ?? '').trim().charAt(0).toUpperCase() || '?';

const AccountPopover: React.FC<AccountPopoverProps> = ({
  profile,
  onClose,
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const displayName = profile?.name || profile?.username || 'Account';

  return (
    <>
      <div className="flex w-full items-center gap-2 p-1.5">
        <Avatar size="sm">
          <AvatarImage src={profile?.avatar?.url} />
          <AvatarFallback>{initials(displayName)}</AvatarFallback>
        </Avatar>
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-semibold text-marble-bg-700">
            {displayName}
          </p>
          {profile?.email && (
            <p className="truncate text-xs text-marble-bg-500">
              {profile.email}
            </p>
          )}
        </div>
      </div>

      <Separator className="my-1" />

      <button
        type="button"
        className={rowClass}
        onClick={() => {
          navigate('/settings/account');
          onClose();
        }}
      >
        <Settings className="size-4 shrink-0 text-marble-bg-700" />
        <p className="text-sm font-semibold text-marble-bg-700">
          Account settings
        </p>
      </button>

      <button
        type="button"
        className={rowClass}
        onClick={() => {
          dispatch(UserLogout());
          dispatch(AccountLogout());
          onClose();
        }}
      >
        <LogOut className="size-4 shrink-0 text-marble-bg-700" />
        <p className="text-sm font-semibold text-marble-bg-700">Sign out</p>
      </button>
    </>
  );
};

export default AccountPopover;
